import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { ESTIMATE_STATUS_OPTIONS } from "../../../../constants/estimateStatus";
import { AuthContext } from "../../../../contexts/AuthContext";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import Modal from "../../components/Modal";
import {
  ServiceTierRows,
  SimpleServiceRow,
} from "../../components/ServiceTierRow";
import ExportIcon from "../../../../assets/icons/export.svg";
import { toast } from "react-toastify";
import { getBackendUrl } from "../../../../utils/env";
import { buildPrintHTML } from "../../bao-phi/utils/pdfTemplate";
import { buildQuotationBodyHTML } from "../../bao-phi/utils/quotationPdfContent";

const ADJUSTMENT_TYPE_OPTIONS = [
  { value: "Tăng", label: "Tăng" },
  { value: "Giảm", label: "Giảm" },
  { value: "Cập nhật", label: "Cập nhật" }
];

const ESTIMATE_SERVICE_GRID =
  "grid-cols-[2.5fr_1.5fr_1.5fr_1.5fr]";

const toBase64FromPublic = async (path) => {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Không thể tải tài nguyên PDF: ${path}`);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const ReadonlyAdjustmentCard = ({ item }) => {
  return (
    <div className="bg-[#F9FAFB] rounded-[16px] opacity-90 w-full">
      <div className="flex gap-0 items-stretch w-full">
        <div className="grid grid-cols-3 gap-4 px-4 py-4 flex-1 w-full">
          <div className="w-full">
            <label className="block text-xs text-gray-600 mb-2">
              Nội dung điều chỉnh
            </label>
            <CustomInput value={item.reason} disabled height="43px" />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-2">
              Loại điều chỉnh
            </label>
            <CustomSelect
              value={ADJUSTMENT_TYPE_OPTIONS.find(
                (opt) => opt.value === item.type,
              )}
              options={ADJUSTMENT_TYPE_OPTIONS}
              disabled
              height="43px"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-2">Số tiền</label>
            <CustomInput
              value={Number(item.amount).toLocaleString()}
              disabled
              height="43px"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const EstimatePopup = ({
  isOpen,
  onClose,
  data,
  getAdjustmentHistory,
  estimateLoading,
}) => {
  const { token_client } = useContext(AuthContext);

  const [form, setForm] = useState({
    unitPrice: "",
    quantity: "",
    vatPercent: "",
    status: null,
  });

  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(true);

  const [visibleTiers, setVisibleTiers] = useState([0]);
  const [tierQuantities, setTierQuantities] = useState([
    { tierIndex: 0, quantity: 1 },
  ]);
  const [tempTierQuantities] = useState({});
  const tierQtyDebounceRef = useRef({});

  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        unitPrice: data.unitPrice ?? "",
        quantity: data.quantity ?? 1,
        vatPercent: data.vatPercent ?? 0,
        status:
          ESTIMATE_STATUS_OPTIONS.find((opt) => opt.value === data.status) ||
          null,
      });

      if (data.priceRanges && data.priceRanges.length > 0) {
        const activeTiers = data.priceRanges
          .map((range, idx) => (range.quantity > 0 ? idx : -1))
          .filter((idx) => idx !== -1);

        setVisibleTiers(activeTiers.length > 0 ? activeTiers : [0]);

        const initialTierQuantities = data.priceRanges.map((range, idx) => ({
          tierIndex: idx,
          quantity: range.quantity || 1,
        }));
        setTierQuantities(initialTierQuantities);
      }
    }
  }, [data]);

  const fetchAdjustmentHistory = useCallback(async (page = 1) => {
    if (!data?._id || !getAdjustmentHistory) return;

    try {
      setHistoryLoading(true);

      const response = await getAdjustmentHistory(token_client, data._id, {
        page,
        limit: 10,
      });

      const newItems = response.data.map((item) => ({
        id: item._id,
        type: item.adjustmentType,
        amount: item.adjustmentAmount,
        reason: item.adjustmentContent,
        status: item.status,
      }));

      setAdjustmentHistory((prev) =>
        page === 1 ? newItems : [...prev, ...newItems],
      );

      setHistoryHasMore(page < response.pagination.totalPages);
      setHistoryPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  }, [data?._id, getAdjustmentHistory, token_client]);

  useEffect(() => {
    if (isOpen && data?._id && getAdjustmentHistory) {
      setAdjustmentHistory([]);
      setHistoryPage(1);
      setHistoryHasMore(true);
      fetchAdjustmentHistory(1);
    }
  }, [data?._id, fetchAdjustmentHistory, getAdjustmentHistory, isOpen]);

  useEffect(() => {
    const debounceTimers = tierQtyDebounceRef.current;

    return () => {
      Object.values(debounceTimers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  /* ================= CALCULATIONS & HANDLERS ================= */

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString()} đ`;
  };

  const handleDownloadEstimate = async () => {
    if (!data) return;

    try {
      setIsDownloading(true);

      const vatPercent = Number(form.vatPercent || 0);
      const totalAmount = Number(data.totalAmount || 0);
      const serviceSubtotal = Math.round(totalAmount / (1 + vatPercent / 100));
      const vatAmount = totalAmount - serviceSubtotal;
      const priceRanges = data.priceRanges || [];
      const activeRanges = priceRanges
        .map((range, index) => ({ range, index }))
        .filter(({ range }) => Number(range.quantity) > 0);
      const firstRange = activeRanges[0]?.range;
      const serviceId = data.serviceId?._id || data._id || "estimate-service";
      const selectedServices = [
        {
          id: serviceId,
          name: data.serviceId?.serviceName || "Dịch vụ",
          displayPrice: Number(firstRange?.price ?? data.unitPrice) || 0,
          quantity: Number(firstRange?.quantity ?? data.quantity) || 1,
          licenseFee:
            Number(
              firstRange?.licenseFee ??
                firstRange?.certificateFee ??
                firstRange?.issueFee ??
                data.licenseFee ??
                data.certificateFee,
            ) || 0,
          priceTiers: activeRanges.map(({ range }) => range),
          tierQuantities: activeRanges.map(({ range }, tierIndex) => ({
            tierIndex,
            quantity: Number(range.quantity) || 1,
          })),
          customTierNames: activeRanges.reduce((names, { range }, tierIndex) => {
            names[tierIndex] = range.rangeName;
            return names;
          }, {}),
          tierVats: activeRanges.length
            ? activeRanges.map(() => vatPercent)
            : [vatPercent],
        },
      ];
      const visibleTiers = {
        [serviceId]: activeRanges.length
          ? activeRanges.map((_, index) => index)
          : [0],
      };
      let adjustmentsForPdf = adjustmentHistory;

      if (getAdjustmentHistory && data._id) {
        try {
          const historyResponse = await getAdjustmentHistory(
            token_client,
            data._id,
            {
              page: 1,
              limit: 1000,
            },
          );
          adjustmentsForPdf = (historyResponse?.data || []).map((item) => ({
            id: item._id,
            type: item.adjustmentType,
            amount: item.adjustmentAmount,
            reason: item.adjustmentContent,
            status: item.status,
          }));
        } catch (historyError) {
          console.error(
            "Không thể tải đầy đủ lịch sử điều chỉnh để in PDF:",
            historyError,
          );
        }
      }

      const approvedAdjustments = adjustmentsForPdf.filter((adjustment) => {
        const normalizedStatus = String(adjustment?.status || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim();
        const isPending =
          normalizedStatus.includes("cho duyet") ||
          normalizedStatus.includes("pending");

        return !isPending && adjustment?.reason?.trim();
      });
      const bodyHTML = buildQuotationBodyHTML({
        date: new Date().toLocaleDateString("vi-VN"),
        specialist: "",
        email: "",
        phone: "",
        selectedServices,
        visibleTiers,
        adjustments: approvedAdjustments,
        subtotal: serviceSubtotal,
        vatTotal: vatAmount,
        grandTotal: totalAmount,
      });
      const [regularFontBase64, boldFontBase64, watermarkBase64] =
        await Promise.all([
          toBase64FromPublic("/fonts/NotoSerif-Regular.ttf"),
          toBase64FromPublic("/fonts/NotoSerif-Bold.ttf"),
          toBase64FromPublic("/images/watermark-45.png"),
        ]);
      const finalHtml = buildPrintHTML(bodyHTML, "Báo giá dịch vụ", {
        regularFontBase64,
        boldFontBase64,
      });
      const BE_URL = getBackendUrl();

      const response = await fetch(`${BE_URL}/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token_client}`,
        },
        body: JSON.stringify({
          html: finalHtml,
          profileName: `Bao_phi_${data.profileId?.brand || "ho_so"}`,
          watermarkBase64,
          watermarkOpacity: 0.12,
          options: { width: 794, height: 1123, deviceScaleFactor: 2 },
          watermarkMode: "fixed",
        }),
      });

      if (!response.ok) throw new Error("Failed to generate PDF");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bao_phi_${data.profileId?.brand || "ho_so"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Tải báo phí thành công");
    } catch (error) {
      console.error(error);
      toast.error("Không thể tải báo phí");
    } finally {
      setIsDownloading(false);
    }
  };

  /* ================= RENDER ================= */

  if (!isOpen || !data) return null;

  const vatPercent = Number(form.vatPercent || 0);
  const priceRanges = data.priceRanges || [];
  const hasTiers = priceRanges.length > 0;

  // Read-only mode - always use totalAmount from API
  const totalAmount = Number(data.totalAmount || 0);
  const serviceSubtotal = Math.round(totalAmount / (1 + vatPercent / 100));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Chi phí hồ sơ"
      maxWidth="900px" // Adjusted to match max-w-4xl roughly

      // Footer via Props
      cancelButtonText="Đóng"

      primaryButtonText={
        <>
          <img
            src={ExportIcon}
            alt=""
            aria-hidden="true"
            className="h-5 w-5 brightness-0 invert"
          />
          Tải xuống báo phí
        </>
      }
      primaryButtonOnClick={handleDownloadEstimate}
      isLoading={isDownloading}
    >
      {estimateLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="">
          {/* Service Table */}
          <div className="mb-6">
            <div className={`px-4 grid ${ESTIMATE_SERVICE_GRID} gap-2 py-3 text-sm font-semibold text-gray-600 border-b`}>
              <div>Tên dịch vụ</div>
              <div>Đơn giá</div>
              <div className="text-center">Số lượng</div>
              <div className="text-right">Thành tiền</div>
            </div>

            {/* Service Rows using shared component */}
            {hasTiers ? (
              <ServiceTierRows
                visibleTiers={visibleTiers}
                tiers={priceRanges}
                serviceName={data.serviceId?.serviceName}
                tierQuantities={tierQuantities}
                tempTierQuantities={tempTierQuantities}
                isReadOnly={true}
                formatCurrency={formatCurrency}
                gridClass={ESTIMATE_SERVICE_GRID}
                showActionColumn={false}
              />
            ) : (
              <SimpleServiceRow
                serviceName={data.serviceId?.serviceName}
                unitPrice={data.unitPrice}
                quantity={form.quantity}
                formatCurrency={formatCurrency}
                gridClass={ESTIMATE_SERVICE_GRID}
                showActionColumn={false}
              />
            )}
          </div>

          {/* ===== Adjustment History – INFINITE SCROLL ===== */}
          {adjustmentHistory.length > 0 && (
            <div
              className="px-4 py-4 flex flex-col gap-3 w-full max-h-[300px] overflow-y-auto"
              onScroll={(e) => {
                const el = e.currentTarget;
                const isBottom =
                  el.scrollHeight - el.scrollTop <= el.clientHeight + 20;

                if (isBottom && historyHasMore && !historyLoading) {
                  fetchAdjustmentHistory(historyPage + 1);
                }
              }}
            >
              {adjustmentHistory.map((item) => (
                <div key={item.id} className="pb-4 border-b">
                  <ReadonlyAdjustmentCard key={item.id} item={item} />
                </div>
              ))}

              {historyLoading && (
                <div className="flex justify-center py-3">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}

          {/* VAT + TOTAL */}
          <div className="p-4">
            <div className="flex flex-col gap-6 px-2 py-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 font-medium">Tạm tính</span>
                <span className="font-medium">
                  {serviceSubtotal.toLocaleString()} ₫
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-700 font-medium">VAT</span>
                <span className="font-medium">{vatPercent}%</span>
              </div>

              <div className="flex justify-between text-sm border-t pt-4">
                <span className="text-gray-700 font-medium">Tổng cộng</span>
                <span className="font-semibold text-[#0267FF] leading-6 text-[16px]">
                  {totalAmount.toLocaleString()} ₫
                </span>
              </div>
            </div>
          </div>

          {/* Disclaimer moved to bottom of body content */}
          <div className="px-6 pb-6">
            <p className="text-sm text-red-600 italic">
              Lưu ý: Chi phí này mang tính chất tham khảo, chi phí chính xác sẽ được
              hệ thống cập nhật lại.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};
